'use client'

import { useState } from 'react'
import { Cell } from '@vinted/web-ui'

import { ErrorItem, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  addAddress,
  AddressEntryType,
  AddressField,
  AddressFieldConfiguration,
  AddressFieldTranslationModel,
  AddressFormType,
  AddressModel,
  addShippingAddress,
  addShippingOrderAddress,
  transformAddress,
} from '@marketplace-web/address/address-data'

import {
  AddressScreenTrackingType,
  buyerPrecheckoutEvent,
  clickEvent,
} from '@marketplace-web/complaints/complaints-data'

import AddressForm from '../AddressForm'

type Props = {
  transactionId: number
  isPending: boolean
  address: AddressModel | null | undefined
  addressFormType: AddressFormType
  screenTrackingType?: AddressScreenTrackingType
  fieldConfiguration?: Array<AddressFieldConfiguration>
  translations?: AddressFieldTranslationModel
  styling?: React.ComponentProps<typeof Cell>['styling']
  shippingOrderId?: number | null
  onSubmit: (address: AddressModel) => void
  onCancel: () => void
}

const ShippingAddress = ({
  transactionId,
  isPending,
  address,
  addressFormType,
  screenTrackingType,
  fieldConfiguration = [
    { field: AddressField.Country },
    { field: AddressField.Name },
    { field: AddressField.Line1 },
    { field: AddressField.Line2 },
    { field: AddressField.PostalCode },
    { field: AddressField.City },
  ],
  translations,
  styling,
  shippingOrderId,
  onSubmit,
  onCancel,
}: Props) => {
  const { track } = useTracking()

  const [submitState, setSubmitState] = useState<UiState>(UiState.Idle)
  const [errors, setErrors] = useState<Array<ErrorItem>>([])

  const trackBuyerPrecheckout = () => {
    if (!transactionId) return
    if (addressFormType !== AddressFormType.CheckoutAddress) return

    track(buyerPrecheckoutEvent({ transactionId }))
  }

  const trackSaveAddressClick = ({ valid }: { valid: boolean }) => {
    if (!transactionId) return
    if (addressFormType !== AddressFormType.CheckoutAddress) return

    track(
      clickEvent({
        screen: 'billing_address_update',
        target: 'save_address',
        targetDetails: JSON.stringify({
          valid,
          type: screenTrackingType,
          transaction_id: transactionId,
        }),
      }),
    )
  }

  const handleAddShippingOrderAddress = async (newAddress: AddressModel) => {
    const response = await addAddress({
      address: { ...newAddress, entryType: AddressEntryType.Shipping },
      skipShippingCallback: true,
    })

    if ('errors' in response) return response
    if (!shippingOrderId) return response

    const {
      user_address: { id: addressId },
    } = response

    const shippingOrderResponse = await addShippingOrderAddress({ addressId, shippingOrderId })

    if ('errors' in shippingOrderResponse) {
      return shippingOrderResponse
    }

    return response
  }

  const handleSubmit = async (newAddress: AddressModel, isFormValid: boolean) => {
    trackBuyerPrecheckout()

    if (!isFormValid) {
      trackSaveAddressClick({ valid: isFormValid })

      return
    }

    setSubmitState(UiState.Pending)
    setErrors([])

    const response = shippingOrderId
      ? await handleAddShippingOrderAddress(newAddress)
      : await addShippingAddress({
          transactionId,
          address: newAddress,
        })

    if ('errors' in response) {
      trackSaveAddressClick({ valid: false })

      const { code, message } = response

      let generalFieldErrors: Array<ErrorItem> = []

      if (message && code === ResponseCode.CountryChangeNotAllowed) {
        generalFieldErrors = [
          {
            field: AddressField.Country,
            value: response.message,
          },
        ]
      }

      setSubmitState(UiState.Failure)
      setErrors([...response.errors, ...generalFieldErrors])

      return
    }

    trackSaveAddressClick({ valid: true })
    setSubmitState(UiState.Success)

    onSubmit(transformAddress(response.user_address))
  }

  const renderLoader = () => (
    <ContentLoader
      styling={ContentLoader.Styling.Wide}
      size={ContentLoader.Size.Large}
      testId="shipping-address-loader"
    />
  )

  const renderAddressForm = () => (
    <AddressForm
      styling={styling}
      defaultValues={address}
      errors={errors}
      fieldConfiguration={fieldConfiguration}
      translations={translations}
      loader={renderLoader()}
      isSubmitPending={isPending || submitState === UiState.Pending}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  )

  return renderAddressForm()
}

export default ShippingAddress
